// import { AxiosResponse } from 'axios';
import { AxiosResponse } from 'axios';
import { PaginationAndFilter } from '../interfaces/common/pagination';
import axiosClient from '../utils/axios-client';
import { throttle } from '../config';
import type { ModelMaster, AddUpdateModel } from '../interfaces/masters/ModelMaster/model';
import { applyFilters } from '../utils/apply-filters';
import { applyPagination } from '../utils/apply-pagination';
import { applySort } from '../utils/apply-sort';
import { wait } from '../utils/wait';

interface Options {
  filters?: Array<{ operator: string; property: string; value: any }>;
  sort?: string;
  sortBy?: string;
  page?: number;
  query?: string;
  view?: string;
}

class ModelApi {
  async getModels(options: Options):
  Promise<{ models: ModelMaster[], modelsCount: number }> {
    if (throttle) {
      await wait(throttle);
    }

    const model : ModelMaster[] = await axiosClient.get('master/model',).then((response) => (
      response.data
    ));

    const { filters, sort, sortBy, page, query, view } = options;

    /*
     NOTE: Query, filter, sort and pagination are operation meant to be executed on the server.
     Since this does not connect to a real backend, we simulate these operations.
     */

    const queriedModels = model.filter((_model) => {
      if (!!query && !_model.productType.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }

      // No need to look for any resource fields
      if (typeof view === 'undefined' || view === 'all') {
        return true;
      }

      // In this case, the view represents the resource status
      return _model.categoryId === view;
    });
    const filteredModels = applyFilters(queriedModels, filters);
    const sortedModels = applySort(filteredModels, sort, sortBy);
    const paginatedModels = applyPagination(sortedModels, page);

    return Promise.resolve({
      models: paginatedModels,
      modelsCount: filteredModels.length
    });
  }

  async getAllModels():Promise<ModelMaster[]> {
    if (throttle) {
      await wait(throttle);
    }

    const models : ModelMaster[] = await axiosClient.get('master/model',).then((response) => (
      response.data
    ));

    return Promise.resolve(models);
  }

  async getDistinctSeries():Promise<[]> {
    if (throttle) {
      await wait(throttle);
    }

    const models : [] = await axiosClient.get('master/model/getDistinctSeries',).then((response) => (
      response.data
    ));

    return Promise.resolve(models);
  }

  async getModelNumbers():Promise<[]> {
    if (throttle) {
      await wait(throttle);
    }

    const models : [] = await axiosClient.get('master/model/getModelNumbers',).then((response) => (
      response.data
    ));

    return Promise.resolve(models);
  }

  async getModel(id): Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.get(`master/model/getById/${id}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getByModelNumber(number): Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.get(`master/model/getByOptions?modelNo=${number}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls POST api to add model in the database
  async addModel(model: AddUpdateModel): Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/model', model).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls PUT api to update model
  async updateModel(model: AddUpdateModel): Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.put('master/model', model).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls DELETE api to delete model in the database
  async deleteModel(modelId: string): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.delete(`master/model/${modelId}`);

    return response;
  }

  // get models based on options filter
  async getModelByOptions(options: any):
  Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/model/getByOptions', { params: options });

    return response;
  }

  async getModelsByPaginationAndFilter(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/model/getByPaginationAndFilter', options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getModelsFilterData():Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/model/filtersData');

    return response;
  }
}

export const modelApi = new ModelApi();
